// ============================================================
// CharacteristicTestGrid - 3×3 grid of clickable characteristic
// buttons. Clicking rolls a d100 test (via the parent's roll
// handler) at the currently selected difficulty.
//
// Unnatural ×N badges: with editableUnnatural, clicking a badge
// (without rolling) opens a −/+ stepper popover; the ✎ chip
// reveals faint ×1 badges on attributes without a multiplier.
// Overrides are TRANSIENT (sheet rolls only - never the stored
// character, never attacks/reactions) and show amber.
// ============================================================
import { memo, useMemo, useState, type ReactNode } from 'react';
import clsx from '../utils/clsx';
import { onActivateKey } from '../utils/keyActivation';
import styles from '../styles/ActionSheet.module.css';
import { CHARACTERISTIC_TEST_LABELS } from '../engine/characteristicTests';
import { getEffectiveUnnaturalMultipliers } from '../engine/character/combatStatsResolver';
import type { Character, CharacteristicId } from '../engine/character/types';

export type UnnaturalOverrides = Partial<Record<CharacteristicId, number>>;

interface CharacteristicTestGridProps {
  character: Character;
  onRoll: (charId: CharacteristicId) => void;
  /** Right-click: opposed vs the sheet's opponent. Absent = inert. */
  onOpposed?: ((charId: CharacteristicId) => boolean) | null;
  /** Enables badge editing. Provide together with unnaturalOverrides + onOverrideChange. */
  editableUnnatural?: boolean;
  unnaturalOverrides?: UnnaturalOverrides;
  /** null clears the override for that characteristic. */
  onOverrideChange?: (charId: CharacteristicId, multiplier: number | null) => void;
}

const CHAR_IDS = Object.keys(CHARACTERISTIC_TEST_LABELS) as CharacteristicId[];
const MAX_MULT = 5;

function CharacteristicTestGrid({
  character,
  onRoll,
  onOpposed = null,
  editableUnnatural = false,
  unnaturalOverrides,
  onOverrideChange,
}: CharacteristicTestGridProps) {
  const [editing, setEditing] = useState(false);
  const [popoverFor, setPopoverFor] = useState<CharacteristicId | null>(null);
  // Stored multipliers floored by trait effects (e.g. Astartes str/tgh ×2)
  const traitMults = useMemo(() => getEffectiveUnnaturalMultipliers(character), [character]);
  const mults: MultEditing = {
    editable: editableUnnatural,
    editing,
    overrides: unnaturalOverrides,
    traitMults,
    popoverFor,
    togglePopover: (id) => setPopoverFor((p) => (p === id ? null : id)),
    closePopover: () => setPopoverFor(null),
    onOverrideChange,
  };
  return (
    <div>
      {editableUnnatural && (
        <div className={styles.unnaturalEditRow}>
          <button
            type="button"
            className={clsx(styles.unnaturalEditToggle, editing && styles.unnaturalEditToggleActive)}
            aria-label="Edit unnatural multipliers"
            title="Edit unnatural ×N multipliers for sheet rolls (transient - the character is never changed)"
            onClick={() => {
              setEditing((e) => !e);
              setPopoverFor(null);
            }}
          >
            ✎ ×N
          </button>
        </div>
      )}
      <div className={styles.charGrid} data-testid="characteristic-test-grid">
        {CHAR_IDS.map((id) => (
          <CharButton key={id} id={id} value={character.characteristics[id].value} {...{ onRoll, onOpposed, mults }} />
        ))}
      </div>
    </div>
  );
}

/** Everything a button needs to show and edit its unnatural multiplier. */
interface MultEditing {
  editable: boolean;
  /** The ✎ chip is on: every characteristic shows a badge. */
  editing: boolean;
  overrides?: UnnaturalOverrides;
  traitMults: Partial<Record<CharacteristicId, number>>;
  popoverFor: CharacteristicId | null;
  togglePopover: (id: CharacteristicId) => void;
  closePopover: () => void;
  onOverrideChange?: (charId: CharacteristicId, multiplier: number | null) => void;
}

/** The transient override, else the stored/trait-floored multiplier, else ×1. */
function effectiveMult(m: MultEditing, id: CharacteristicId): number {
  return m.overrides?.[id] ?? m.traitMults[id] ?? 1;
}

interface CharButtonProps {
  id: CharacteristicId;
  value: number;
  onRoll: (charId: CharacteristicId) => void;
  onOpposed: ((charId: CharacteristicId) => boolean) | null;
  mults: MultEditing;
}

/** One characteristic: click rolls a test, right-click an opposed one; the badge edits its multiplier. */
function CharButton({ id, value, onRoll, onOpposed, mults }: CharButtonProps) {
  const label = CHARACTERISTIC_TEST_LABELS[id];
  const mult = effectiveMult(mults, id);
  const overridden = mults.overrides?.[id] != null;
  const showBadge = mult > 1 || overridden || (mults.editable && mults.editing);
  const opposedHint = onOpposed ? ' - right-click for an opposed test vs the current target' : '';
  return (
    <button
      type="button"
      className={styles.charBtn}
      title={`Roll a ${label} test (d100 vs ${value} + modifiers)` + opposedHint}
      aria-label={`Roll ${label} test`}
      onClick={() => onRoll(id)}
      onContextMenu={(e) => {
        if (!onOpposed) return;
        e.preventDefault();
        onOpposed(id);
      }}
    >
      <span className={styles.charLabel}>{label}</span>
      <span className={styles.charBtnRight}>
        <span className={styles.charValue}>{value}</span>
        {showBadge && <MultBadge id={id} label={label} mult={mult} overridden={overridden} mults={mults} />}
        <span className={styles.charBonus}>({Math.floor(value / 10)})</span>
        {mults.popoverFor === id && <MultStepper id={id} label={label} mult={mult} mults={mults} />}
      </span>
    </button>
  );
}

interface MultProps {
  id: CharacteristicId;
  label: string;
  mult: number;
  mults: MultEditing;
}

/** The ×N badge; when editable, a press opens the stepper without rolling. */
function MultBadge({ id, label, mult, overridden, mults }: MultProps & { overridden: boolean }) {
  if (!mults.editable) return <span className={styles.charMult}>×{mult}</span>;
  const toggle = () => mults.togglePopover(id);
  return (
    <span
      className={clsx(
        styles.charMult,
        styles.charMultEditable,
        overridden && styles.charMultOverridden,
        mult <= 1 && !overridden && styles.charMultFaint,
      )}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label} unnatural multiplier`}
      onClick={(e) => {
        e.stopPropagation();
        toggle();
      }}
      onContextMenu={(e) => {
        // The badge edits multipliers; it must not fire
        // the button's opposed roll underneath.
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) =>
        onActivateKey(() => {
          e.stopPropagation();
          toggle();
        })(e)
      }
    >
      ×{mult}
    </span>
  );
}

/**
 * The −/+/✕ stepper. Matching the stored/trait-floored value clears the
 * override (the badge goes back to normal); ✕ clears it outright.
 */
function MultStepper({ id, label, mult, mults }: MultProps) {
  const setMult = (next: number) => {
    const stored = mults.traitMults[id] ?? 1;
    const clamped = Math.min(MAX_MULT, Math.max(1, next));
    mults.onOverrideChange?.(id, clamped === stored ? null : clamped);
  };
  const reset = () => {
    mults.onOverrideChange?.(id, null);
    mults.closePopover();
  };
  return (
    // Mouse-only by the user's choice (2026-09-27): the stepper keeps clicks off the roll button it sits in.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <span
      className={styles.multPopover}
      role="group"
      aria-label={`${label} unnatural multiplier stepper`}
      onClick={(e) => e.stopPropagation()}
    >
      <StepperKey label={`Decrease ${label} multiplier`} onPress={() => setMult(mult - 1)}>
        −
      </StepperKey>
      <span className={styles.multPopoverValue}>×{mult}</span>
      <StepperKey label={`Increase ${label} multiplier`} onPress={() => setMult(mult + 1)}>
        +
      </StepperKey>
      <StepperKey label={`Reset ${label} multiplier`} onPress={reset}>
        ✕
      </StepperKey>
    </span>
  );
}

function StepperKey({ label, onPress, children }: { label: string; onPress: () => void; children: ReactNode }) {
  return (
    <span
      role="button"
      tabIndex={0}
      className={styles.multPopoverBtn}
      aria-label={label}
      onClick={onPress}
      onKeyDown={onActivateKey(onPress)}
    >
      {children}
    </span>
  );
}

export default memo(CharacteristicTestGrid);
